import styled, { css } from "styled-components";
import { Link as ReactRouterLink } from "react-router-dom";

import Grid from "core/Grid";
import { focusOutline } from "core/links";
import FormInput from "core/FormInput";
import PaymentMethodRadioButtons from "./PaymentMethodRadioButtons";
import { SHARED_FORM_STYLES } from "core/Form";
import Flexbox from "core/Flexbox";
import { P, BorderBottomLinkStyle, CustomFocusStyle } from "core/Typography";
import { Col } from "core/oldGrid";
import breakpoints from "core/breakpoints";
import { spacingWithBreakpoints } from "core/spacing";
import colors from "core/colors";
import BodyText from "core/BodyText";
import { easingSeconds } from "core/animation";
import { mediumFamily, regularFamily } from "core/fontStyles";
import Heading from "core/Heading";
import Button from "core/Button";
import PaypalPaymentContent from "./PaypalPaymentContent";
import Messages from "core/Messages";

import Question from "core/ReusableIcons/Question";

import { STYLE_TYPE_DEFAULTS } from "core/Link/styles";
import Link from "core/Link";

import { convertPxToRem } from "frontend/components/helpers";
import { handleTopSpacing, handleBottomSpacing } from "helpers/spacingAndFonts";

export const FormHeading = styled(Heading).attrs({
    type: "h2",
    size: "xs",
    bottomSpacing: "lg",
    headingColor: colors.secondary.orca
})``;

// CardLinks component:
export const FormButtonBodyText = styled(BodyText)`
    &:hover {
        color: ${colors.secondary.cosmic};
        border-color: ${colors.secondary.cosmic};
    }

    ${BorderBottomLinkStyle}
    ${CustomFocusStyle}
`;

export const CardLinksFlexbox = styled(Flexbox)`
    height: 1.438rem; /* 23px */
    margin-bottom: 1.5rem; /* 24px */
`;

// OrderSummary component:
export const OrderItem = styled(Flexbox)`
    ${handleBottomSpacing({ bottomSpacing: "lg" })}
    ${handleTopSpacing({ topSpacing: "lg" })}
`;

export const TotalCharge = styled(Flexbox)`
    border-top: ${convertPxToRem({ px: 1 })} solid ${colors.primary.scheifer};
    ${handleTopSpacing({ topSpacing: "lg" })}

    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible),
    &:active {
        outline: none;
        box-shadow: none;
        border: none;
    }

    &:focus {
        ${focusOutline}
    }
`;

export const SubsOrderItem = styled(BodyText).attrs({
    size: "sm",
    type: "p"
})`
    ${handleBottomSpacing({ bottomSpacing: "xl" })}
    ${handleTopSpacing({
        topSpacing: "lg"
    })}


    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible),
    &:active {
        outline: none;
        box-shadow: none;
        border: none;
    }

    &:focus {
        ${focusOutline}
    }
`;

export const StyledOrderSummary = styled.div`
    ${spacingWithBreakpoints.XL2TopAndBottom}
`;

// StripeFormInput component:
export const StripeFormInputContainer = styled.div`
    label {
        display: block;
    }
`;

export const SuccessIconWrapper = styled.div`
    opacity: 0;
    position: absolute;
    right: 0.8875rem;
    top: 50%;
    margin-top: -0.625rem;
    transition: all ${easingSeconds};
`;

export const InputWrapper = styled.div`
    position: relative;

    .StripeElement {
        ${SHARED_FORM_STYLES.BASE}
        transition: box-shadow ${easingSeconds};
        padding-top: ${convertPxToRem({ px: 11 })};
        padding-bottom: ${convertPxToRem({ px: 11 })};

        &:hover {
            ${SHARED_FORM_STYLES.HOVER}
        }

        &--focus {
            &,
            &:hover {
                ${SHARED_FORM_STYLES.FOCUS}
            }
        }

        &--complete:not(.StripeElement--focus) + ${SuccessIconWrapper} {
            opacity: 1;
        }
    }

    .input-complete + ${SuccessIconWrapper} {
        opacity: 1;
    }
`;

// used in EmptyForm but is for StripeFormInput-
// styles wont reflect via the style object because Stripe forms use object style format & other various differences:
export const stripeElementStyles = {
    base: {
        fontSize: "16px", // using REM makes it look much smaller
        color: colors.secondary.orca,
        ":disabled": {
            color: colors.secondary.oolong
        },
        "::placeholder": {
            color: colors.secondary.oolong,
            transition: `all ${easingSeconds}`
        },
        ":focus": {
            "::placeholder": {
                color: "transparent"
            }
        }
    },
    invalid: {
        color: colors.functional.lightMode.sangria
    }
};

// FormInputLayout:
export const ReceiptInfoHelpIcon = styled(Question).attrs({
    size: "md",
    fillColor: colors.secondary.oolong,
    "data-tip": "show and hide",
    "data-event": "click",
    "data-event-off": "blur"
})`
    margin-left: 0.375rem; /* 6px*/
    cursor: pointer;
`;

export const ReceiptInfoTextWrapper = styled(Flexbox).attrs({
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center"
})``;

export const MinusIcon = styled.div`
    background-color: ${colors.secondary.oolong};
    width: 0.75rem; /* 12px */
    height: 0.125rem; /* 2px */
`;

export const VatContainer = styled(Flexbox).attrs({
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center"
})`
    margin-top: ${convertPxToRem({ px: 24 })};
`;

export const PlusMinusButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0 ${convertPxToRem({ px: 8 })} 0 0;
    background: none;
    width: 1rem; /* 16px */
    height: 1rem; /* 16px */

    ${({ showReceiptInfoField }) =>
        showReceiptInfoField &&
        css`
            margin-bottom: ${convertPxToRem({ px: 24 })};
        `}
`;

export const StyledCheckoutForm = styled.div`
    form {
        width: 100%;
    }

    ${PaymentMethodRadioButtons}, ${PaypalPaymentContent} {
        margin-bottom: ${convertPxToRem({ px: 24 })};
    }
`;

export const FixedWidthCol = styled(Col)`
    width: 100%;

    @media (min-width: ${breakpoints.tablet.rem}) {
        width: ${({ width }) => width};
    }
`;

export const GeneralFormError = styled(P).attrs({ size: 12 })`
    color: ${colors.functional.lightMode.laser};
    margin-bottom: 0.5rem;
`;

export const CardNumberGrid = styled(Grid)``;

export const MonthYearGrid = styled(Grid)`
    width: 7.188rem; /* 115px */
    margin-right: ${convertPxToRem({ px: 16 })};
`;

export const CvcGrid = styled(Grid)`
    width: 4.938rem; /* 79px */
`;

export const NameGrid = styled(Grid)``;

export const PostalGrid = styled(Grid)`
    width: 8.375rem; /* 134px */
`;

export const OptionalBodyText = styled(BodyText)`
    margin-left: 0.1875rem; /* 3px */
`;

export const VatGrid = styled(Grid)`
    ${FormInput} {
        margin-bottom: 0; // to target the VAT field when it's open
    }
`;

// OrderPreview component:
export const OrderPreviewContainer = styled.div`
    ${spacingWithBreakpoints.XL2Bottom}
`;

export const OrderPreviewContent = styled.div`
    padding: ${convertPxToRem({ px: 24 })} 0;
    border-top: 1px solid ${colors.primary.pigeon};
    border-bottom: 1px solid ${colors.primary.pigeon};
`;

export const OrderPreviewDisplay = styled.div`
    a {
        text-decoration: none;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
`;

export const Title = styled.div`
    flex: 1 1;
    font-family: ${regularFamily};
    font-size: ${convertPxToRem({ px: 16 })};
    line-height: ${convertPxToRem({ px: 24 })} span {
        color: ${colors.secondary.orca};
        transition: color ${easingSeconds}, border-bottom-color ${easingSeconds};
        &:hover {
            color: ${colors.secondary.oolong};
            border-bottom: 1px solid ${colors.secondary.oolong};
        }
    }

    @media (max-width: ${breakpoints.beforeTablet.rem}) {
        font-family: ${mediumFamily};
        font-size: ${convertPxToRem({ px: 13 })};
        line-height: ${convertPxToRem({ px: 19.2 })};
    }
`;

export const Thumbnail = styled.img`
    flex: 0 0;
    max-width: 5.875rem; /* 94px */
    max-height: 5.875rem; /* 94px */
    margin-right: ${convertPxToRem({ px: 16 })};
    width: auto;
    height: auto;
    transition: all ${easingSeconds};

    &:hover {
        opacity: 0.6;
    }
`;

// OrderTotal component:
export const OrderTotalContainer = styled.div`
    ${spacingWithBreakpoints.XL2TopAndBottom}
`;

export const OrderTotalContents = styled.div`
    border-top: 1px solid ${colors.primary.pigeon};
    padding-top: ${convertPxToRem({ px: 24 })};
    display: flex;
    justify-content: space-between;
    align-items: baseline;
`;

export const UsdBodyText = styled(BodyText)`
    margin-right: 0.5rem; /* 8px */
`;
/* -------------------------------------------------------------------------- */
/*                            CHECKOUT FORM BUTTONS                           */
/* -------------------------------------------------------------------------- */

export const PurchaseButtonContainer = styled(Grid).attrs({
    item: true,
    sm: 9,
    md: 6,
    lg: 6
})``;

export const StyledPurchaseButton = styled(Button).attrs({
    styleType: "cta",
    width: "100%"
})``;

export const CancelLinkContainer = styled(Grid).attrs({
    item: true,
    sm: 3,
    md: 6,
    lg: 6
})`
    text-align: right;
`;

export const CheckoutFormButtonsContainer = styled(Grid).attrs({
    mobileSpacing: 0,
    tabletSpacing: 0,
    desktopSpacing: 0,
    alignItems: "center",
    justifyContent: "flex-start"
})``;

/* -------------------------------------------------------------------------- */
/*                                 PAYPAL STUFF                               */
/* -------------------------------------------------------------------------- */

export const PaypalContentContainer = styled.div`
    // only show focus on keyboard not with mouse:
    &:focus:not(.focus-visible),
    &:active {
        outline: none;
        box-shadow: none;
        border: none;
    }

    &:focus {
        ${focusOutline}
    }
`;

export const PayPalPurchaseTitle = styled(BodyText).attrs({
    type: "p",
    copyColor: colors.secondary.onyx,
    size: "lg"
})`
    ${handleBottomSpacing({ bottomSpacing: "sm" })}
`;

export const PromoCodePayPalWarning = styled(Messages).attrs({
    infoType: "info",
    styleType: "solid",
    isDismissable: false
})``;
